import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetProviderDirectoryOrganizationQuery,
  useUpdateProviderDirectoryOrganizationMutation,
} from '@/services/api/providerDirectoryV2Service';

const ORG_TYPES = [
  'HOSPITAL_NETWORK',
  'CLINIC_GROUP',
  'SOLO_PRACTICE',
  'BILLING_COMPANY',
  'RECORDS_COMPANY',
  'PHARMACY_CHAIN',
  'OTHER',
] as const;

export const EditOrganizationModal = ({
  open,
  onClose,
  organizationId,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
}) => {
  const { data: org } = useGetProviderDirectoryOrganizationQuery(
    { organizationId },
    { skip: !open || !organizationId },
  );

  const [updateOrg, { isLoading: isSaving }] =
    useUpdateProviderDirectoryOrganizationMutation();

  const [name, setName] = useState('');
  const [organizationType, setOrganizationType] = useState<string>('OTHER');
  const [aliasesText, setAliasesText] = useState('');
  const [website, setWebsite] = useState('');

  useEffect(() => {
    if (!open || !org) return;
    setName(org.name ?? '');
    setOrganizationType(org.organization_type ?? 'OTHER');
    setAliasesText((org.aliases ?? []).join(', '));
    setWebsite(org.website ?? '');
  }, [open, org]);

  const aliases = useMemo(() => {
    const raw = aliasesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return raw.length ? raw : [];
  }, [aliasesText]);

  const canSave = Boolean(name.trim());

  const handleSave = async () => {
    if (!org) return;
    if (!canSave) return;
    try {
      await updateOrg({
        organizationId: org.id,
        patch: {
          name: name.trim(),
          organization_type: organizationType || null,
          aliases,
          website: website.trim() || null,
        },
      }).unwrap();
      toast.success('Organization updated');
      onClose();
    } catch (err) {
      console.error('Failed to update org', err);
      toast.error('Failed to update organization');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="w-[calc(100vw-24px)] max-w-2xl max-h-[calc(100vh-24px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit organization</DialogTitle>
          <DialogDescription>
            Update the organization details used for grouping locations.
          </DialogDescription>
        </DialogHeader>

        {!org ? (
          <div className="text-sm text-zinc-600">Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="text-xs font-medium text-zinc-700">Name</div>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="text-xs font-medium text-zinc-700">Type</div>
                <select
                  className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm w-full"
                  value={organizationType}
                  onChange={(e) => setOrganizationType(e.target.value)}
                >
                  {ORG_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-medium text-zinc-700">Website</div>
                <Input
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="Optional…"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-medium text-zinc-700">Aliases</div>
              <Input
                value={aliasesText}
                onChange={(e) => setAliasesText(e.target.value)}
                placeholder="Comma-separated…"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!canSave || isSaving}>
                Save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
